import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";

import {
  DataTable,
  ColumnVisibilityDropdown,
  type Table
} from "@/components/datatable";
import {
  SearchFilterBar,
  useFilterBuilder,
  serializeFiltersToQuery,
  type FilterableAttribute
} from "@/components/search-filter-bar";
import {
  AddProcessData,
  DeleteProcessData,
  ProcessesList,
  ProcessFieldMetadata,
  UpdateProcessData
} from "@/hooks/services/pps/handlers/process-api";

import {
  REFERENCE_TABLE,
  useForeignKeyData
} from "@/hooks/use-foreign-key-data";

import { useGetData } from "@/hooks/services/use-query-hooks";
import { useDebounce } from "@/hooks/use-debounce";

import { capitalize } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { AddProcessSheet } from "./components/add-process-sheet";

import { createFileRoute } from "@tanstack/react-router";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { toastManager } from "@/lib/toast-manager";

export const Route = createFileRoute(
  "/plant-3/(processes-screens)/processes-management/processes"
)({
  component: RouteComponent
});

function RouteComponent() {
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState<any>(null);
  const [tableInstance, setTableInstance] = useState<Table<any> | null>(null);
  const [hasTableData, setHasTableData] = useState(false);

  const processFieldMetadata = useGetData(ProcessFieldMetadata({}));
  const [refetch, setRefetch] = useState<() => void>(() => {});

  const { optionsData: shopEditOptions, setSearch: setShopEditSearch } =
    useForeignKeyData(REFERENCE_TABLE.ppsShop, "shopName");

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);

  const { filters, ...filterActions } = useFilterBuilder();

  const debouncedFilters = useDebounce(filters, 300);

  const handleRefetch = (callback: () => void) => {
    setRefetch(() => callback);
  };

  // Build filterable/sortable attributes from metadata
  const filterableAttributes = useMemo<FilterableAttribute[]>(() => {
    return (
      processFieldMetadata?.data?.data?.map((item: any) => ({
        id: item.name,
        label: capitalize(item.label),
        dataType: item.dataType
      })) || []
    );
  }, [processFieldMetadata?.data?.data]);

  const columns = useMemo<ColumnDef<any, unknown>[]>(() => {
    return (
      processFieldMetadata?.data?.data?.map((item: any) => {
        const column = {
          header: capitalize(item?.label || ""),
          accessorKey: item?.name,
          type: item.dataType,
          attribute: item.name,
          referenceTable: item.foreignKeyEntity || "",
          editable: true
        } as ColumnDef<any, unknown>;

        return column;
      }) || []
    );
  }, [processFieldMetadata?.data?.data]);

  const handleEditValue = (
    value: Record<string, string | number>,
    id: number,
    setEditingCell: (...args: any) => void
  ) => {
    const values = Object.entries(value);
    let body: Record<string, string | number> = {};

    if (values.length > 0) {
      const [fieldName, fieldValue] = values[0];
      // Map field names to the expected API format
      if (fieldName === "processName") {
        body.processName = fieldValue;
      } else if (fieldName === "shopName") {
        body.shopId = fieldValue;
      }
    }

    UpdateProcessData(id, body)
      .then(() => {
        setEditingCell(null);
        if (refetch) refetch();
        toastManager.showSuccess(
          "Process updated successfully",
          "The process has been updated."
        );
      })
      .catch((error: any) => {
        toastManager.showError(
          "Failed to update process",
          error?.message || "An unexpected error occurred."
        );
      });
  };

  const handleDeleteRow = (id: number) => {
    DeleteProcessData(id)
      .then(() => {
        if (refetch) refetch();
        toastManager.showSuccess(
          "Process deleted successfully",
          "The process has been removed."
        );
      })
      .catch((error: any) => {
        toastManager.showError(
          "Failed to delete process",
          error?.message || "An unexpected error occurred."
        );
      });
  };

  const handleConfirmDelete = () => {
    if (selectedProcess) {
      handleDeleteRow(selectedProcess.id);
      setDeleteDialogOpen(false);
      setSelectedProcess(null);
    }
  };

  const handleForeignKeySearch = (
    referenceTable: string,
    search: string,
    callback: (options: { label: string; value: string | number }[]) => void
  ) => {
    const normalizedRef = referenceTable.toLowerCase();

    if (normalizedRef === REFERENCE_TABLE.ppsShop.toLowerCase()) {
      setShopEditSearch(search);
      callback(shopEditOptions);
    } else {
      callback([]);
    }
  };

  return (
    <>
      <SearchFilterBar
        placeholder="Search processes..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        dynamicFilters={filters}
        filterableAttributes={filterableAttributes}
        {...filterActions}
        showCreateButton
        onCreateClick={() => setOpen(true)}
        createButtonLabel="Add Process"
      />

      <Card className="aero:!rounded-lg aero:!border-neutral-200 aero:!p-0 aero:!gap-0 aero:!shadow-none aero:min-w-0">
        <CardHeader className="aero:!p-4">
          <div className="aero:flex aero:flex-col aero:lg:flex-row aero:items-start aero:justify-between aero:gap-4">
            <div>
              <h2 className="aero:text-base aero:md:text-lg aero:font-bold aero:leading-7 aero:tracking-normal aero:text-foreground">
                Process List
              </h2>
              <p className="aero:text-xs aero:md:text-sm aero:text-neutral-500 aero:leading-5 aero:tracking-normal">
                Manage and configure production processes
              </p>
            </div>

            <div className="aero:flex aero:items-center aero:gap-2 aero:flex-shrink-0">
              {tableInstance && hasTableData && (
                <ColumnVisibilityDropdown table={tableInstance} />
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-200 aero:my-4" />
        </CardHeader>

        <CardContent>
          <DataTable<any, any>
            api={(params) =>
              ProcessesList({
                ...params,
                search: debouncedSearch || undefined,
                filters: serializeFiltersToQuery(debouncedFilters)
              })
            }
            columns={columns || []}
            filterParams={{
              search: debouncedSearch,
              filters: serializeFiltersToQuery(debouncedFilters)
            }}
            identifierKey="id"
            onRefetch={handleRefetch}
            onEditValue={(value, id, callback) => {
              handleEditValue(value, id, callback);
            }}
            onForeignKeySearch={handleForeignKeySearch}
            onTableReady={(table) => {
              setTableInstance(table);
              setHasTableData(table.getRowModel().rows.length > 0);
            }}
            features={{
              enableSelection: false,
              enableEditing: true,
              enablePinning: true,
              enableResizing: true,
              enableColumnDragAndDrop: false,
              enableRowDragAndDrop: false,
              enableSorting: true,
              enablePagination: true
            }}
            actionItems={[
              {
                title: "Delete",
                className: "aero:bg-transparent",
                icon: (
                  <Trash2 className="aero:h-4 aero:w-4 aero:text-destructive" />
                ),
                onClick: (row) => {
                  setSelectedProcess(row);
                  setDeleteDialogOpen(true);
                }
              }
            ]}
            pageSizes={[10, 20, 50, 100]}
            defaultPageSize={10}
            emptyState={"No processes found"}
          />
        </CardContent>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="aero:sm:max-w-[600px] aero:p-6 aero:gap-0">
            <DialogHeader className="aero:mb-4">
              <DialogTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
                Delete Process
              </DialogTitle>
            </DialogHeader>

            <div className="aero:w-full aero:h-0 aero:border-t aero:border-neutral-400 aero:mb-4" />

            <p className="aero:text-sm aero:text-neutral-700">
              You are about to permanently delete the process{" "}
              <strong>'{selectedProcess?.processName}'</strong>. This action
              cannot be undone.
            </p>

            <DialogFooter className="aero:mt-6 aero:pt-4 aero:border-t aero:border-neutral-200">
              <DialogClose asChild>
                <Button
                  type="button"
                  intent="neutral"
                  design="outline"
                  className="aero:h-10 aero:gap-2 aero:px-4"
                >
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                intent="destructive"
                design="solid"
                className="aero:h-10 aero:gap-2 aero:px-4"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Process Dialog */}
        <AddProcessSheet
          open={open}
          onOpenChange={setOpen}
          onSubmit={(data, form) => {
            AddProcessData(data)
              .then(() => {
                if (refetch) refetch();
                form.reset();
                setOpen(false);
                toastManager.showSuccess(
                  "Process created successfully",
                  "The new process has been added."
                );
              })
              .catch((error: any) => {
                toastManager.showError(
                  "Failed to create process",
                  error?.message || "An unexpected error occurred."
                );
              });
          }}
        />
      </Card>
    </>
  );
}
